import * as Sentry from "@sentry/nextjs";
import { getEnvPublic } from "@remi/environment";
import { getBaseSentryConfig, setErrorReporter } from "@remi/sentry";

import { addAxiomContextToSentryEvent } from "../axiom/addAxiomContextToSentryEvent";
import { dropImpersonated403 } from "./dropImpersonated403";
import { dropSuppressibleAblyError } from "./dropSuppressibleAblyError";
import { fingerprintAblyClientIdMismatch } from "./fingerprintAblyClientIdMismatch";
import { fingerprintAuthenticatedError } from "./fingerprintAuthenticatedError";
import { fingerprintTrpcPermissionError } from "./fingerprintTrpcPermissionError";
import { NEXTJS_DATA_COLLECTION, stripRequestData } from "./strictSentryPrivacy";
import { buildIntegrations } from "./utils/buildIntegrations";
import { chainBeforeSend } from "./utils/chainBeforeSend";
import { chainBeforeSendTransaction } from "./utils/chainBeforeSendTransaction";
import { filterTransactionsWithHealthySampling } from "./utils/filterTransactionsWithHealthySampling";

export function initSentryNextjs(sentryOptions?: Sentry.NodeOptions) {
  const baseConfig = getBaseSentryConfig();
  const { integrations, beforeSend, beforeSendTransaction, ...restOptions } = sentryOptions ?? {};
  const client = Sentry.init({
    ...baseConfig,
    dsn: getEnvPublic("NEXT_PUBLIC_SENTRY_DSN"),
    tracePropagationTargets: [], // OTEL handles trace propagation
    dataCollection: NEXTJS_DATA_COLLECTION,
    integrations: buildIntegrations(baseConfig.integrations, integrations),
    beforeSend: chainBeforeSend(
      baseConfig.beforeSend,
      stripRequestData,
      dropImpersonated403,
      dropSuppressibleAblyError,
      addAxiomContextToSentryEvent,
      fingerprintAuthenticatedError,
      fingerprintAblyClientIdMismatch,
      fingerprintTrpcPermissionError,
      beforeSend,
    ),
    beforeSendTransaction: chainBeforeSendTransaction(filterTransactionsWithHealthySampling, beforeSendTransaction),
    ...restOptions,
  });

  setErrorReporter({
    captureException: Sentry.captureException,
    metrics: Sentry.metrics,
    withScope: Sentry.withScope,
    setUser: (user) => Sentry.getIsolationScope().setUser(user),
    setContext: Sentry.setContext,
  });

  return client;
}
